import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import LocationSelector from '../../../components/LocationSelector';
import Map from '../../../components/Map';
import FormInput from '../../../components/FormInput';
import { useAuth } from '../../../context/Auth';
import { centralAPI } from '../../../services/central-api';
import { dispatchService } from '../../../services/central-api/dispatch';
import { translate } from '../../../services/translate';
import { WindowInformation } from '../../../services/window-information';
import { AlreadyExists } from '../../../services/central-api/base-service';
import ActingBodiesDropdown from '../OccurrenceTypes/ActingBodiesDropdown';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { DispatchParamList } from '../../../typings/Params';
import { RootNavigation } from '../../../Routes';

export default function CityPropertyForm({ navigation }: {
    navigation: RootNavigation;
}) {
    const route = useRoute<RouteProp<DispatchParamList, 'CityPropertyForm'>>();
    const cityPropertyId = route.params?.id;
    const windowInfo = WindowInformation();
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [name, setName] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [type, setType] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [pin, setPin] = useState<LatLng>();

    async function loadPageInfo() {
        try {
            if (cityPropertyId) {
                const cityProperty = await dispatchService.getCityProperty(cityPropertyId);

                if (cityProperty) {
                    setName(cityProperty.name);
                    setCode(cityProperty.code);
                    setType(cityProperty.type);
                    setActingBodyId(cityProperty.actingBodyId);

                    if (cityProperty.latitude && cityProperty.longitude) {
                        setPin({ lat: cityProperty.latitude, lng: cityProperty.longitude });
                        fetchAddress({ lat: cityProperty.latitude, lng: cityProperty.longitude });
                    }
                }
            }
        } catch (err) {
            console.error(err);
        }
    }

    async function fetchAddress(pos: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pos.lat, longitude: pos.lng });
        if (address) {
            setAddress(address);
        }
    }

    const isFocused = useIsFocused();
    useEffect(() => {
        if (isFocused) {
            loadPageInfo();
        }
    }, [route.params, isFocused]);

    function isEmpty(value: string) {
        return value == '';
    }

    function isFormValid() {
        const areMainRequirementsValid = !isEmpty(name) && !isEmpty(address) && pin?.lat && pin?.lng;

        const isActingBodyRequired = authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_city_property;

        if (isActingBodyRequired && !actingBodyId) {
            return false;
        }

        return areMainRequirementsValid;
    }

    function cleanValues() {
        setName('');
        setAddress('');
        setCode('');
        setType('');
        setPin({ lat: 0, lng: 0 });
    }

    async function formSubmit() {
        try {
            if (!pin || !pin.lat || !pin.lng) {
                return;
            }

            if (cityPropertyId) {
                await dispatchService.updateCityProperty({
                    id: cityPropertyId,
                    name,
                    code,
                    type,
                    latitude: pin.lat,
                    longitude: pin.lng,
                    actingBodyId: (actingBodyId || authenticatedUser?.actingBodyUnit.actingBodyId) ?? 0
                });
            } else {
                await dispatchService.createCityProperty({
                    name,
                    code,
                    type,
                    latitude: pin.lat,
                    longitude: pin.lng,
                    actingBodyId: (actingBodyId || authenticatedUser?.actingBodyUnit.actingBodyId) ?? 0
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            cleanValues();
            navigation.navigate('Dispatch', { screen: 'CityProperties' });
        } catch (err) {
            console.error(err);
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }

            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const handleGoBack = () => {
        cleanValues();
        navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Dispatch', { screen: 'CityProperties' });
    };

    return (
        <Container>
            <Header
                title={translate('registerCityProperty')}
                goBackAction={handleGoBack}
                actions={
                    <CustomButton
                        text={translate('save')}
                        disabled={!isFormValid()}
                        onPress={formSubmit}
                    />
                }
            />

            <Card>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={[styles.leftArea, !windowInfo.isMobile ? { height: '100%' } : undefined]}>
                        {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_city_property) &&
                            <ActingBodiesDropdown
                                labelFontSize={14}
                                includeAllOption={false}
                                value={actingBodyId}
                                setValue={setActingBodyId}
                                zIndex={2}
                                disabled={!!cityPropertyId}
                            />
                        }
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={name}
                            labelFontSize={14}
                            onChangeText={setName}
                        />
                        <FormInput
                            label={translate('code')}
                            placeholder={translate('code')}
                            value={code}
                            labelFontSize={14}
                            onChangeText={setCode}
                        />
                        <FormInput
                            label={translate('type')}
                            placeholder={translate('type')}
                            value={type}
                            labelFontSize={14}
                            onChangeText={setType}
                        />
                        <LocationSelector
                            address={address}
                            onAddressChange={setAddress}
                            label={translate('location')}
                            labelFontSize={14}
                            onPinChange={pos => setPin(pos)}
                        />
                    </View>

                    <View
                        style={[windowInfo.isMobile ? {
                            width: '100%',
                            height: '50%',
                        } : {
                            flex: 3,
                            height: '100%',
                        }]}>
                        <View style={styles.card(windowInfo.isMobile)}>
                            <Map
                                dynamicMarker={pin}
                                useDynamicMarker={true}
                                markerChanged={pos => {
                                    fetchAddress(pos);
                                    setPin(pos);
                                }}
                            />
                        </View>
                    </View>
                </View>
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    leftArea: {
        borderWidth: 1,
        paddingHorizontal: 15,
        paddingVertical: 5,
        borderColor: theme.colors.card.border,
        overflowY: 'scroll',
        backgroundColor: theme.colors.card.background,
        borderRadius: 4,
        flex: 1,
        gap: 10,
    },
    card: (isMobile: boolean) => ({
        backgroundColor: theme.colors.card.background,
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 4,
        paddingVertical: 10,
        paddingHorizontal: 20,
        flex: 1,
        height: isMobile ? 200 : undefined
    }),
    formContainer: (isMobile: boolean) => ({
        flex: 1,
        flexDirection: isMobile ? 'column' : 'row',
        borderColor: theme.colors.container.border,
        gap: 16,
    }),
}));
